/**
 * Terms of Service page — ai-tokens.me (end-user-facing).
 */

import React, { useEffect } from 'react';
import { FileText } from 'lucide-react';

export default function TermsOfServicePage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="container" style={{ maxWidth: 800, padding: '48px 24px', marginTop: 64 }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em' }}>
          <FileText size={22} style={{ verticalAlign: 'text-bottom', marginRight: 8, color: 'var(--accent)' }} />
          Terms of Service
        </h1>
        <p style={{ color: 'var(--text-dim)', marginTop: 4, fontSize: '0.9rem' }}>Last updated: March 22, 2026</p>
      </div>

      <div style={{ color: 'var(--text)', lineHeight: 1.8, fontSize: '0.95rem' }}>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>1. Acceptance of Terms</h2>
          <p>By accessing or using AI Tokens, including the website ai-tokens.me and related services (collectively, the "Service"), operated by CodeTunez Studios ("we", "us", "our"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, please do not use the Service.</p>
          <p style={{ marginTop: 12 }}>We may update these Terms at any time. Continuing to use the Service after changes are posted means you accept the updated Terms.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>2. What AI Tokens Is</h2>
          <p>AI Tokens is a token-based system that lets you pay for AI features across multiple applications. You purchase tokens through our platform, and those tokens are spent when you use AI-powered features in apps built by developers who have integrated with our platform (token-kit).</p>
          <p style={{ marginTop: 12 }}>We do not build or control the individual apps you use — we provide the token infrastructure that powers their AI features.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>3. Your Account</h2>
          <p>To purchase and use tokens, you need to create an account. You agree to:</p>
          <ul style={{ paddingLeft: 24, marginTop: 8 }}>
            <li>Provide accurate and truthful information when you register</li>
            <li>Keep your login credentials and user tokens secure and confidential</li>
            <li>Tell us immediately if you think someone has access to your account without your permission</li>
            <li>Accept responsibility for everything that happens under your account</li>
          </ul>
          <p style={{ marginTop: 12 }}>We may suspend or close any account that we believe, in our sole judgement, is being misused or violates these Terms.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>4. Purchasing Tokens</h2>
          <ul style={{ paddingLeft: 24, marginTop: 8 }}>
            <li><strong>All sales are final.</strong> Token purchases are non-refundable except where required by applicable law or at our sole discretion.</li>
            <li><strong>Tokens do not expire</strong> under normal use. However, if your account is terminated for violating these Terms, your remaining token balance may be forfeited.</li>
            <li><strong>Prices may change.</strong> We reserve the right to change token package prices at any time. Price changes do not affect tokens you have already purchased.</li>
            <li><strong>Payment processing</strong> is handled by third-party payment providers. We do not store your credit card or payment information.</li>
            <li><strong>Tokens have no cash value</strong> and cannot be exchanged for money, transferred to other accounts, or sold to other people.</li>
          </ul>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>5. How Tokens Are Used</h2>
          <p>When you use an AI feature in a supported app, tokens are automatically deducted from your balance based on:</p>
          <ul style={{ paddingLeft: 24, marginTop: 8 }}>
            <li>The AI model used (different models cost different amounts)</li>
            <li>The length and complexity of your request and the AI's response</li>
          </ul>
          <p style={{ marginTop: 12 }}>Token costs for different models are published in our pricing documentation. You can set daily, monthly, and per-request spending limits to control how quickly your tokens are used.</p>
          <p style={{ marginTop: 12 }}>While we make every effort to accurately track your token balance, minor discrepancies may occasionally occur. We will resolve any such issues in a fair and reasonable manner.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>6. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul style={{ paddingLeft: 24, marginTop: 8 }}>
            <li>Use the Service for anything illegal or harmful</li>
            <li>Generate content that is abusive, threatening, hateful, or harassing</li>
            <li>Attempt to hack, manipulate, or bypass the token system or security measures</li>
            <li>Create fake accounts or use automated systems to abuse the Service</li>
            <li>Resell or transfer your tokens to other people</li>
            <li>Interfere with or disrupt the Service for other users</li>
          </ul>
          <p style={{ marginTop: 12 }}>We may investigate violations and take action including suspending your account and reporting to law enforcement where appropriate.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>7. AI-Generated Content</h2>
          <p>When you use AI features through our tokens, the actual AI processing is performed by third-party providers like OpenAI, Anthropic, and Amazon. You should know that:</p>
          <ul style={{ paddingLeft: 24, marginTop: 8 }}>
            <li>AI can make mistakes — always review AI-generated content before relying on it</li>
            <li>We do not control what AI models produce and are not responsible for the accuracy or reliability of AI responses</li>
            <li>Your inputs are sent to AI providers for processing — please do not include sensitive personal information in your prompts</li>
            <li>AI model availability and behaviour may change over time</li>
          </ul>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>8. Service Availability</h2>
          <p>We do our best to keep the Service running smoothly, but we cannot promise it will always be available. The Service may be temporarily unavailable due to maintenance, updates, or circumstances beyond our control. We are not liable for any interruptions to the Service.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>9. Limitation of Liability</h2>
          <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, CODETUNEZ STUDIOS AND ITS DIRECTORS, EMPLOYEES, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE, INCLUDING BUT NOT LIMITED TO:</p>
          <ul style={{ paddingLeft: 24, marginTop: 8 }}>
            <li>Loss of data, profits, or goodwill</li>
            <li>Inaccurate or undesirable AI-generated content</li>
            <li>Service interruptions or downtime</li>
            <li>Unauthorised access to your account</li>
          </ul>
          <p style={{ marginTop: 12 }}>OUR TOTAL LIABILITY FOR ALL CLAIMS SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS BEFORE THE CLAIM AROSE, OR ONE HUNDRED US DOLLARS ($100), WHICHEVER IS GREATER.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>10. Disclaimer of Warranties</h2>
          <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES, EXPRESS OR IMPLIED. WE DO NOT GUARANTEE THAT THE SERVICE WILL BE ERROR-FREE, UNINTERRUPTED, SECURE, OR THAT AI-GENERATED CONTENT WILL BE ACCURATE OR SUITABLE FOR ANY PURPOSE.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>11. Indemnification</h2>
          <p>You agree to defend, indemnify, and hold harmless CodeTunez Studios from any claims, damages, or expenses (including legal fees) arising from your use of the Service, your violation of these Terms, or your violation of any law or third-party right.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>12. Account Termination</h2>
          <p>We may close or suspend your account at any time, with or without notice, if we believe you have violated these Terms. If your account is terminated due to a violation:</p>
          <ul style={{ paddingLeft: 24, marginTop: 8 }}>
            <li>You lose access to the Service immediately</li>
            <li>Any remaining tokens in your account may be forfeited</li>
            <li>We are not required to provide a refund</li>
          </ul>
          <p style={{ marginTop: 12 }}>You may also delete your own account at any time through the Settings page.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>13. Governing Law</h2>
          <p>These Terms are governed by the laws of the jurisdiction in which CodeTunez Studios is established. Any disputes will be resolved in the courts of that jurisdiction.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>14. Severability</h2>
          <p>If any part of these Terms is found to be invalid or unenforceable, the remaining parts continue to apply in full.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>15. Entire Agreement</h2>
          <p>These Terms, together with our Privacy Policy, make up the entire agreement between you and CodeTunez Studios regarding the Service.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>16. Contact Us</h2>
          <p>If you have questions about these Terms:</p>
          <p style={{ marginTop: 8 }}>
            CodeTunez Studios<br />
            Email: <a href="mailto:legal@codetunezstudios.com" style={{ color: 'var(--accent)' }}>legal@codetunezstudios.com</a>
          </p>
        </section>

      </div>
    </div>
  );
}
